import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DateInput } from "./DateInput";

export function EditEntryDialog({ open, onOpenChange, sessionId, licenseTypes, editEntry, onSuccess, API, mannschaftOptions, lehrgangOptions, username }) {
  // Use provided options or defaults
  const MANNSCHAFT_OPTIONS = mannschaftOptions?.length > 0 ? mannschaftOptions : ["Damen 1", "Damen 2", "Herren 1", "Herren 2", "Herren 3", "Herren 4", "Jugend"];
  const LEHRGANG_OPTIONS = lehrgangOptions?.length > 0 ? lehrgangOptions : ["J-Lehrgang", "D-Lehrgang", "C-Lehrgang", "B/K-Lehrgang", "B-Lehrgang", "D-Fortbildung", "C-Fortbildung", "B-Fortbildung"];

  const [formData, setFormData] = useState({
    vorname: "",
    nachname: "",
    geburtsdatum: "",
    geschlecht: "",
    spieler_lizenz: "",
    schiedsrichter_lizenz: "",
    gueltig_ab: "",
    gueltig_bis: "",
    aktiv: "",
    mannschaft: "",
    lehrgang: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editEntry && editEntry.data) {
      setFormData({
        vorname: editEntry.data.vorname || "",
        nachname: editEntry.data.nachname || "",
        geburtsdatum: editEntry.data.geburtsdatum || "",
        geschlecht: editEntry.data.geschlecht || "",
        spieler_lizenz: editEntry.data.spieler_lizenz || "",
        schiedsrichter_lizenz: editEntry.data.schiedsrichter_lizenz || "",
        gueltig_ab: editEntry.data.gueltig_ab || "",
        gueltig_bis: editEntry.data.gueltig_bis || "",
        aktiv: editEntry.data.aktiv || "",
        mannschaft: editEntry.data.mannschaft || "",
        lehrgang: editEntry.data.lehrgang || ""
      });
    }
  }, [editEntry]);

  const FIELD_LABELS = {
    vorname: "Vorname", nachname: "Nachname", geburtsdatum: "Geburtsdatum",
    geschlecht: "Geschlecht", spieler_lizenz: "Spieler-Lizenz",
    schiedsrichter_lizenz: "SR-Lizenz", gueltig_ab: "Gültig ab",
    gueltig_bis: "Gültig bis", aktiv: "Aktiv", mannschaft: "Mannschaft",
    lehrgang: "Lehrgang",
  };

  const handleSave = async () => {
    if (!formData.vorname || !formData.nachname) {
      return;
    }

    try {
      setSaving(true);
      const original = editEntry.data;
      await axios.put(`${API}/data/update?session_id=${sessionId}`, {
        row_id: original._id,
        ...formData
      });

      // Build detailed change description
      const changes = [];
      for (const key of Object.keys(formData)) {
        const oldVal = (original[key] || "").trim();
        const newVal = (formData[key] || "").trim();
        if (oldVal !== newVal) {
          const label = FIELD_LABELS[key] || key;
          if (!oldVal && newVal) changes.push(`${label} gesetzt: ${newVal}`);
          else if (oldVal && !newVal) changes.push(`${label} entfernt`);
          else changes.push(`${label}: ${oldVal} → ${newVal}`);
        }
      }
      const name = `${formData.vorname} ${formData.nachname}`;
      const desc = changes.length > 0 ? changes.join(", ") : "keine Änderungen";
      toast.success(`${name} - ${desc} (von ${username})`);

      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error("Update error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">
            Eintrag bearbeiten
          </DialogTitle>
          <DialogDescription>
            Bearbeiten Sie die Daten des Eintrags.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Vorname *
              </Label>
              <Input
                data-testid="edit-vorname-input"
                value={formData.vorname}
                onChange={(e) => handleChange("vorname", e.target.value)}
                placeholder="Vorname"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Nachname *
              </Label>
              <Input
                data-testid="edit-nachname-input"
                value={formData.nachname}
                onChange={(e) => handleChange("nachname", e.target.value)}
                placeholder="Nachname"
                className="input-focus"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Geburtsdatum
              </Label>
              <DateInput
                value={formData.geburtsdatum}
                onChange={(v) => handleChange("geburtsdatum", v)}
                testId="edit-geburtsdatum-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Geschlecht
              </Label>
              <Select value={formData.geschlecht || "_none"} onValueChange={(v) => handleChange("geschlecht", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="edit-geschlecht-select">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  <SelectItem value="männlich">männlich</SelectItem>
                  <SelectItem value="weiblich">weiblich</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
              Spieler-Lizenz
            </Label>
            <Select value={formData.spieler_lizenz || "_none"} onValueChange={(v) => handleChange("spieler_lizenz", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="edit-spieler-lizenz-select">
                <SelectValue placeholder="Auswählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Keine</SelectItem>
                {licenseTypes.spieler_lizenzen.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
              Schiedsrichter-Lizenz
            </Label>
            <Select value={formData.schiedsrichter_lizenz || "_none"} onValueChange={(v) => handleChange("schiedsrichter_lizenz", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="edit-sr-lizenz-select">
                <SelectValue placeholder="Auswählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Keine</SelectItem>
                {licenseTypes.schiedsrichter_lizenzen.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Gültig ab
              </Label>
              <DateInput
                value={formData.gueltig_ab}
                onChange={(v) => handleChange("gueltig_ab", v)}
                testId="edit-gueltig-ab-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Gültig bis
              </Label>
              <DateInput
                value={formData.gueltig_bis}
                onChange={(v) => handleChange("gueltig_bis", v)}
                testId="edit-gueltig-bis-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Aktiv
              </Label>
              <Select value={formData.aktiv || "_none"} onValueChange={(v) => handleChange("aktiv", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="edit-aktiv-select">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  <SelectItem value="Ja">Ja</SelectItem>
                  <SelectItem value="Nein">Nein</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Mannschaft
              </Label>
              <Select value={formData.mannschaft || "_none"} onValueChange={(v) => handleChange("mannschaft", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="edit-mannschaft-select">
                  <SelectValue placeholder="Auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  {MANNSCHAFT_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Lehrgang
              </Label>
              <Select value={formData.lehrgang || "_none"} onValueChange={(v) => handleChange("lehrgang", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="edit-lehrgang-select">
                  <SelectValue placeholder="Auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  {LEHRGANG_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            data-testid="update-entry-button"
            onClick={handleSave}
            disabled={saving || !formData.vorname || !formData.nachname}
            className="w-full"
          >
            {saving ? "Wird gespeichert..." : "Änderungen speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
